import React, { useCallback, useEffect, useRef, useState } from "react";
import * as companyService from "../../services/companyCustomerService";

const PAGE_SIZE = 10;
const GRID = "2.4fr 1.3fr 2.2fr 1fr";

function initials(name) {
  const words = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] || "") + (words[1]?.[0] || "")).toUpperCase() || "?";
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" });
}

// The Customers screen's "Companies" tab: B2B bill-to customers (the ones invoices are raised
// against from Branch Jobs), searchable and paged like the individuals list. Click a row to open
// the company.
//   search     what the shared search box holds while this tab is showing
//   onTotal    reports the unfiltered number of companies (for the tab's count)
//   onOpen(c)  the user chose a company
export default function CompaniesTab({ search, onTotal, onOpen }) {
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const latest = useRef(0);

  const load = useCallback(
    async (pageNum, searchVal) => {
      const request = ++latest.current;
      try {
        setLoading(true);
        setError("");
        const res = await companyService.listCompanies({ page: pageNum, limit: PAGE_SIZE, search: searchVal });
        if (request !== latest.current) return; // a newer search / page is on its way
        if (res?.success) {
          setCompanies(res.data || []);
          setPagination(res.pagination || null);
          if (!searchVal && res.pagination) onTotal?.(res.pagination.total);
        } else {
          setError(res?.message || "Could not load companies");
        }
      } catch (err) {
        if (request === latest.current) setError(err?.message || "Could not load companies");
      } finally {
        if (request === latest.current) setLoading(false);
      }
    },
    [onTotal]
  );

  // A new search starts again from page 1, after the user pauses typing.
  const firstRun = useRef(true);
  useEffect(() => {
    const delay = firstRun.current ? 0 : 400;
    firstRun.current = false;
    const t = setTimeout(() => {
      setPage(1);
      load(1, search);
    }, delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const goToPage = (p) => {
    setPage(p);
    load(p, search);
  };

  const totalPages = pagination?.totalPages ?? 1;

  return (
    <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 1px 5px rgba(0,0,0,0.06)", overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: GRID, padding: "12px 24px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
        {["COMPANY", "TRN", "ADDRESS", "ADDED"].map((h) => (
          <div key={h} style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.4 }}>
            {h}
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: "56px 24px", textAlign: "center", color: "#94a3b8", fontSize: 14 }}>Loading companies…</div>
      ) : error ? (
        <div role="alert" style={{ padding: "56px 24px", textAlign: "center", color: "#dc2626", fontSize: 14 }}>
          {error}
        </div>
      ) : companies.length === 0 ? (
        <div style={{ padding: "56px 24px", textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
          {search ? "No companies match your search" : "No companies yet. Companies you invoice from Branch Jobs appear here."}
        </div>
      ) : (
        companies.map((c, i) => (
          <div
            key={c.id}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(c)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onOpen(c))}
            style={{
              display: "grid",
              gridTemplateColumns: GRID,
              padding: "14px 24px",
              alignItems: "center",
              borderBottom: i < companies.length - 1 ? "1px solid #f8fafc" : "none",
              cursor: "pointer",
              background: "#fff",
            }}
          >
            <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: "#fef3c7", color: "#b45309", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                {initials(c.name)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>{c.phone_number || "—"}</div>
                <div style={{ fontSize: 11.5, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email || ""}</div>
              </div>
            </div>
            <div style={{ fontSize: 12.5, color: "#475569", wordBreak: "break-all" }}>{c.trn || "—"}</div>
            <div style={{ fontSize: 12.5, color: "#475569", paddingRight: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.address || "—"}</div>
            <div style={{ fontSize: 12.5, color: "#475569" }}>{formatDate(c.created_at)}</div>
          </div>
        ))
      )}

      {!loading && !error && companies.length > 0 && (
        <div style={{ padding: "16px 24px", borderTop: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <span style={{ fontSize: 13, color: "#64748b" }}>
            {pagination ? `Page ${page} of ${totalPages} · ${pagination.total} total companies` : `Showing ${companies.length} companies`}
          </span>
          {totalPages > 1 && (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button
                aria-label="Previous page"
                onClick={() => goToPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                style={{ padding: "6px 12px", border: "1.5px solid #e2e8f0", borderRadius: 7, fontSize: 13, fontWeight: 600, background: page <= 1 ? "#f8fafc" : "#fff", color: page <= 1 ? "#cbd5e1" : "#334155", cursor: page <= 1 ? "default" : "pointer" }}
              >
                ‹
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, idx) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p = start + idx;
                if (p > totalPages) return null;
                return (
                  <button
                    key={p}
                    onClick={() => goToPage(p)}
                    aria-current={p === page ? "page" : undefined}
                    style={{ padding: "6px 12px", border: `1.5px solid ${p === page ? "#4f46e5" : "#e2e8f0"}`, borderRadius: 7, fontSize: 13, fontWeight: p === page ? 700 : 500, background: p === page ? "#eef2ff" : "#fff", color: p === page ? "#4f46e5" : "#334155", cursor: "pointer", minWidth: 32 }}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                aria-label="Next page"
                onClick={() => goToPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                style={{ padding: "6px 12px", border: "1.5px solid #e2e8f0", borderRadius: 7, fontSize: 13, fontWeight: 600, background: page >= totalPages ? "#f8fafc" : "#fff", color: page >= totalPages ? "#cbd5e1" : "#334155", cursor: page >= totalPages ? "default" : "pointer" }}
              >
                ›
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
